import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

function MyGames() {
  const games = [
    {
      id: 'cat-watching-game',
      title: 'Cat Watching Game',
      emoji: '🐱',
      description: 'Motion detection game where a cat watches you play!',
      thumbnail: '🐱👀',
      color: 'from-pink-100 to-purple-100',
      featured: true,
    },
    {
      id: 'tiger-deer-game',
      title: 'Tiger & Deer Game',
      emoji: '🐯',
      description: 'An exciting chase game with AI players',
      thumbnail: '🐯🦌',
      color: 'from-orange-100 to-yellow-100',
      featured: false,
    },
    {
      id: 'egg-catcher-game',
      title: 'Egg Catcher Game',
      emoji: '🥚',
      description: 'Catch falling eggs - fun and addictive!',
      thumbnail: '🥚✨',
      color: 'from-yellow-100 to-orange-100',
      featured: false,
    },
    {
      id: 'motion-detection-buzzer',
      title: 'Motion Detection Buzzer',
      emoji: '⚡',
      description: 'Interactive game using motion sensors',
      thumbnail: '⚡🎯',
      color: 'from-blue-100 to-purple-100',
      featured: false,
    },
    {
      id: 'qt-clock-game',
      title: 'QT Clock Game',
      emoji: '⏰',
      description: 'Learn time with this fun interactive clock game',
      thumbnail: '⏰🕐',
      color: 'from-green-100 to-blue-100',
      featured: false,
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <div className="w-full min-h-screen" style={{ background: 'linear-gradient(to bottom right, #3B0764, #6B21A8, #7C3AED, #8B5CF6)' }}>
      {/* Header Section */}
      <section className="py-16 md:py-20 px-4" style={{ background: 'linear-gradient(to bottom, rgba(59,7,100,0.9), rgba(107,33,168,0.9))' }}>
        <motion.div
          className="container mx-auto max-w-5xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 font-heading drop-shadow-lg"
              style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '1px #0F0A1A', textShadow: '3px 3px 0 #0F0A1A' }}>
            🎮 MY GAMES 🎮
          </h1>
          <p className="text-xl text-yellow-100 max-w-2xl font-bold" style={{ fontFamily: "'Fredoka One', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
            Click any game to explore and learn how it works!
          </p>
        </motion.div>
      </section>

      {/* Games Grid */}
      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12"
            variants={container}
            initial="hidden"
            animate="visible"
          >
            {games.map((game) => (
              <Link key={game.id} to={`/game/${game.id}`}>
                <motion.div
                  variants={item}
                  className="flex flex-col items-center"
                >
                  {/* Title Above */}
                  <h3 className="text-4xl font-black mb-6 text-center font-heading" style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '1px #0F0A1A' }}>
                    {game.title}
                  </h3>

                  {/* Horizontal Oval Card */}
                  <motion.div
                    whileHover={{ y: -4, boxShadow: '6px 6px 0 #0F0A1A, 0 15px 30px -5px rgba(0, 0, 0, 0.3)' }}
                    className="group cursor-pointer w-96 h-48 overflow-hidden shadow-lg transition-all flex items-center justify-center"
                    style={{ 
                      borderRadius: '50% / 45%',
                      border: '4px solid #0F0A1A',
                      boxShadow: '6px 6px 0 #0F0A1A'
                    }}
                  >
                    {/* Thumbnail Section */}
                    <div
                      className={`relative bg-gradient-to-br ${game.color} w-full h-full flex items-center justify-center overflow-hidden`}
                    >
                      {/* Animated background elements */}
                      <motion.div
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 4, repeat: Infinity }}
                        className="text-5xl opacity-90 group-hover:opacity-100 transition-opacity"
                      >
                        {game.thumbnail}
                      </motion.div>

                      {/* Featured Badge */}
                      {game.featured && (
                        <div className="absolute top-1 right-1 bg-yellow-300 text-gray-800 px-2 py-0.5 rounded-full text-xs font-bold">
                          ✨
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-16 md:py-20 px-4" style={{ background: 'linear-gradient(to bottom, rgba(59,7,100,0.9), rgba(107,33,168,0.9))' }}>
        <motion.div
          className="container mx-auto max-w-3xl text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-black text-yellow-300 mb-3 font-heading drop-shadow-lg"
              style={{ fontFamily: "'Bangers', cursive", color: '#FDE047', WebkitTextStroke: '1px #0F0A1A', textShadow: '2px 2px 0 #0F0A1A' }}>
            🚀 MORE COMING SOON! 🚀
          </h2>
          <p className="text-lg text-yellow-100 font-bold" style={{ fontFamily: "'Fredoka One', cursive", color: '#FDE047', WebkitTextStroke: '0.5px #0F0A1A' }}>
            I'm always working on new projects. Follow me to stay updated!
          </p>
        </motion.div>
      </section>
    </div>
  );
}

export default MyGames;
